import type { ReploShopifyProduct } from "replo-runtime/shared/types";

import * as React from "react";

import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import { getSellingPlanOptionsFromProduct } from "@v1/utils/sellingPlans";
import { docs } from "@v2/utils/docs";
import { Info } from "lucide-react";
import { isDynamicDataValue } from "replo-runtime/shared/utils/dynamic-data";

import { DynamicDataSelector } from "../../DynamicDataSelector";

type SellingPlansSelectorProps = {
  product: ReploShopifyProduct;
  sellingPlanId: string | number | null;
  allowThirdPartySellingPlan?: boolean;
  onChange: (sellingPlanId: string | number | null) => void;
  onRemove(): void;
  onChangeThirdPartySellingPlan: (checked: boolean) => void;
  side?: "left" | "right" | "top" | "bottom";
  sideOffset?: number;
};

export const SellingPlansSelector: React.FC<SellingPlansSelectorProps> = ({
  product,
  sellingPlanId,
  allowThirdPartySellingPlan,
  onChangeThirdPartySellingPlan,
  onChange,
  onRemove,
  side = "left",
  sideOffset = 16,
}) => {
  const sellingPlanOptions = getSellingPlanOptionsFromProduct(product, false);
  const selectedSellingPlan = sellingPlanOptions.find(
    (option) => Number(option.value) === Number(sellingPlanId),
  );

  if (sellingPlanOptions.length === 0) {
    return null;
  }

  // Common template value logic for both cases
  const templateValue =
    sellingPlanId && isDynamicDataValue(String(sellingPlanId))
      ? String(sellingPlanId)
      : "{{attributes_selectedSellingPlan}}";

  // NOTE (Jackson, 2025-04-03): Externlly, sellingPlanId is a number, but
  // with respect to the combobox, it needs to be a string. Additionally,
  // use "no-selection" as a sentinel value to indicate that no selling plan
  // is selected.
  const comboboxValue = sellingPlanId ? String(sellingPlanId) : "no-selection";

  // Common switch component for both cases
  const thirdPartySwitch = (
    <div className="mt-1 flex w-full flex-row justify-between">
      <SwitchWithDescription
        isOn={
          allowThirdPartySellingPlan || allowThirdPartySellingPlan == undefined
        }
        onChange={onChangeThirdPartySellingPlan}
        size="sm"
        label={
          <>
            Allow third-party embeds to override selling plan?{" "}
            <a
              href={docs.sellingPlans.thirdPartyAtc}
              className="text-primary inline-flex cursor-pointer items-center text-xs"
              target="_blank"
              rel="noreferrer"
              aria-label="Learn More"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Info size={12} aria-hidden />
            </a>
          </>
        }
      />
    </div>
  );

  return (
    <DeprecatedLabeledControl label="Selling Plan" size="sm">
      {isDynamicDataValue(String(sellingPlanId)) ? (
        <DynamicDataSelector
          side="left"
          sideOffset={16}
          initialPath={templateValue?.split("") ?? []}
          targetType="sellingPlan"
          trigger={
            <DynamicDataValueIndicator
              type="other"
              templateValue={templateValue}
              onRemove={onRemove}
            />
          }
          onChange={(value: string) => {
            onChange(value);
          }}
        />
      ) : (
        <div className="flex flex-1 gap-2">
          <Combobox
            title="Select Selling Plan 2"
            side={side}
            sideOffset={sideOffset}
            options={sellingPlanOptions}
            value={comboboxValue}
            onChange={(value: string | undefined) => {
              if (value === "no-selection" || !value) {
                onChange(null);
              } else {
                onChange(Number(value));
              }
            }}
            layoutClassName="w-full"
            trigger={
              <Combobox.SelectionButton
                title={
                  typeof selectedSellingPlan?.label === "string"
                    ? selectedSellingPlan.label
                    : "Select Selling Plan"
                }
                placeholder="Select Selling Plan"
                size="sm"
                titleAlignment="start"
                endEnhancer={
                  comboboxValue !== "no-selection" ? (
                    <FormFieldXButton
                      onClick={() => {
                        onRemove();
                      }}
                    />
                  ) : undefined
                }
              />
            }
          />
          <DynamicDataSelector
            side="left"
            sideOffset={218}
            targetType="sellingPlan"
            trigger={<DynamicDataButton />}
            onChange={(value: string) => {
              onChange(value);
            }}
          />
        </div>
      )}
      {thirdPartySwitch}
    </DeprecatedLabeledControl>
  );
};
